/**
 * LearnTipsToggle
 *
 * A small, persistent graduation-cap affordance fixed to the bottom-right of
 * the viewport that turns the Learn Tips help-badge overlay on and off.
 * There's no header-level Learn Mode toggle in this app's shared
 * `createShell()` chrome the way there is in the portal, so this toggle is
 * self-contained and app-local (see the sweep report for the promotion note
 * — this is exactly the kind of control a future cross-app "Learn Mode"
 * primitive in `@plerity/dashboard-shell` could absorb).
 *
 * Stacked directly above `@plerity/chat-widget`'s `ChatBubble` FAB
 * (`fixed right-6 z-50`, `h-14 w-14`, `bottom-6` + safe-area) rather than
 * sharing its corner — `bottom-24 right-6` clears the FAB's 56px height plus
 * its own `bottom-6` offset with room to spare, same `right-6` edge so the
 * two stack tidily instead of overlapping (QA #12 precedent:
 * `__tests__/a11y/modalAboveChatFab.test.ts`). Same `z-50` tier as the FAB
 * itself — the two never occupy the same screen region, so there's no
 * z-fight to resolve, just a position that keeps both independently
 * clickable. Like the FAB it adds the shell's `--shell-fab-safe-area` var to
 * its offset, so a fixed bottom bar that sets it lifts both together.
 *
 * State persists to `localStorage` via `useLearnTipsStore` — no backend
 * round trip, so it doesn't carry across devices, which is an acceptable
 * trade-off for a help affordance.
 */

import { GraduationCap } from "lucide-react";
import { useImmersiveStore } from "../../stores/immersiveStore";
import { useLearnTipsStore } from "../../stores/learnTipsStore";

export default function LearnTipsToggle() {
  const enabled = useLearnTipsStore((s) => s.enabled);
  const toggle = useLearnTipsStore((s) => s.toggle);
  // Immersive routes (video editor) hide the toggle below lg — on phones it
  // covered the editor's bottom-sheet tabs. Desktop keeps it.
  const immersive = useImmersiveStore((s) => s.immersive);

  return (
    <button
      type="button"
      onClick={toggle}
      aria-pressed={enabled}
      aria-label={enabled ? "Turn off help tips" : "Turn on help tips"}
      title={
        enabled ? 'Help tips: on — showing "?" badges across the app' : 'Help tips — show "?" badges across the app'
      }
      className={[
        // @allow-arbitrary: bottom-24 plus the shell FAB safe-area var, so this
        // toggle rises with the chat FAB when a fixed bottom bar (calendar bulk
        // actions) sets --shell-fab-safe-area. Defaults to 0px = plain bottom-24.
        // @allow-css-var: --shell-fab-safe-area is set at runtime by createShell (dashboard-shell), 0px fallback
        "fixed bottom-[calc(6rem+var(--shell-fab-safe-area,0px))] right-6 z-50 flex h-10 w-10 items-center justify-center rounded-full shadow-lg",
        "transition-colors duration-150",
        immersive ? "max-lg:hidden" : "",
        // Explicit per-theme ring-offset color (not just ring-offset-2) —
        // the default ring-offset-color is white, which was invisible
        // against this button's own light-mode `bg-white` fill (2026-10-04
        // QA finding: no visible focus indicator when Tab-focused).
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-focus focus-visible:ring-offset-2 focus-visible:ring-offset-gray-100 dark:focus-visible:ring-offset-gray-900",
        enabled
          ? "bg-brand text-brand-fg hover:bg-brand-hover dark:bg-brand-500 dark:hover:bg-brand-600"
          : "bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200",
      ].join(" ")}
    >
      <GraduationCap className="h-5 w-5" aria-hidden="true" />
    </button>
  );
}
